<template>
	<section :class="outerContainerClasses">
		<div :class="innerContainerClasses">
			<article class="display-flex flex-wrap-wrap gap-150-md gap-300-lg justify-content-center">
				<header class="display-none-md col-12">
					<Badge
						v-if="props.caseStudy.caseStudyBadgeLabel"
						class="mb-50"
						background="slate"
						:text="props.caseStudy.caseStudyBadgeLabel"
						size="large"
						icon-name="discover"
					/>
					<h2 v-if="overline" class="overline mb-100">{{ overline }}</h2>
					<h3 class="heading-2 font-weight-normal line-height-125 mb-100" v-html="heading" />
				</header>
				<figure
					class="venture-case-study__cover-img col-12 col-6-md col-5-lg m-0"
					:style="imageUrl"
					:title="imageAlt"
					role="presentation"
				/>

				<div class="venture-case-study__cover-content col-12 col-5-md py-150-md">
					<Badge
						v-if="props.caseStudy.caseStudyBadgeLabel"
						background="slate"
						:text="props.caseStudy.caseStudyBadgeLabel"
						size="large"
						icon-name="discover"
						class="display-none-smo mb-50"
					/>
					<h2 v-if="overline" class="display-none-smo overline mb-100">{{ overline }}</h2>
					<h3 class="heading-2 font-weight-normal line-height-125 mb-100 display-none-smo" v-html="heading" />
					<p class="body-md" v-html="summary"> </p>
					<BaseCtaLink
						v-if="ctaLink"
						class="mt-100"
						:link="ctaLink"
						icon="arrow-bold"
						:data-test="`venture-case-study-cta-${ctaLink?.text}`"
					/>
				</div>
			</article>
		</div>
	</section>
</template>

<script setup lang="ts">
import Badge from '@components/badges/badge-component.vue';
import { computed } from 'vue';
import { Link } from 'types/layout';
import { SanityMarketingCaseStudy } from 'types/sanity/marketing-components';
import { useSanityImage } from '@utils/composables/use-sanity-image';

interface Props {
	backgroundColor?: string;
	caseStudy: SanityMarketingCaseStudy;
	fullWidth?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
	backgroundColor: 'bg-color-bg-tertiary',
	fullWidth: true
});

const outerContainerClasses = computed((): string =>
	props.fullWidth
		? `venture-case-study panel--full-width py-400 py-500-smo ${props.backgroundColor}`
		: 'venture-case-study container-lg'
);
const innerContainerClasses = computed((): string =>
	props.fullWidth ? 'container' : 'py-400 py-500-smo bg-color-bg-secondary px-150 px-0-lg'
);

const overline = computed((): string => props.caseStudy?.caseStudyOverline ?? '');
const heading = computed((): string => props.caseStudy?.caseStudyHeading ?? '');
const summary = computed((): string => props.caseStudy?.caseStudySummary ?? '');
const ctaLink = computed((): Link | null => {
	if (props.caseStudy.caseStudyLink?.url) {
		return {
			text: props.caseStudy.caseStudyLink?.linkText ?? 'Learn More',
			href: props.caseStudy.caseStudyLink?.url,
			target: props.caseStudy.caseStudyLink.openInNewTab ? '_blank' : '_self'
		};
	}
	return null;
});
const imageUrl = computed((): string => {
	const optimizedUrl = useSanityImage(props.caseStudy?.caseStudyImage?.asset.url, 'hero');
	return optimizedUrl ? `background-image: url(${optimizedUrl})` : '';
});

const imageAlt = computed((): string => props.caseStudy?.caseStudyImage?.asset?.altText ?? '');
</script>

<style scoped lang="scss">
@use '../../../styles/constants/colors' as *;
@use '../../../styles/utilities/respond-to.scss' as *;

.venture-case-study {
	&.panel--full-width {
		.venture-case-study__cover-img,
		.venture-case-study__cover-content {
			@include respond-to(md) {
				max-width: 432px;
			}
		}
	}

	.venture-case-study__cover-img {
		@include respond-to(md) {
			max-width: 432px;
		}
	}
}

.heading-2 {
	&.font-weight-normal {
		font-weight: 400;
	}
	&.line-height-125 {
		line-height: 1.25;
	}
}

.venture-case-study__cover-img {
	background-color: gray(30);
	background-size: cover;
	background-position: center;
	min-height: 228px;
}
</style>
